/**
 * Enter Your Reality - Daily Entry Experience
 * The PRIMARY entry point for the Abundance system
 * 
 * This is NOT a vision board editor.
 * This is a daily subconscious conditioning system.
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  Heart, 
  Sun, 
  Home, 
  Compass, 
  Zap,
  Settings,
  TrendingUp,
  Waves
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reality States - the core experiences
const REALITY_STATES = [
  {
    id: 'wealth',
    title: 'Wealth',
    subtitle: 'Expand your receiving',
    phrase: 'It is safe for me to receive more',
    icon: Sparkles,
    color: '#CBA153',
    gradient: 'from-amber-900/30 via-stone-900/50 to-[#121210]',
    image: 'https://images.unsplash.com/photo-1635445616419-cbf46ad8f0aa?w=800&q=80',
  },
  {
    id: 'love',
    title: 'Love',
    subtitle: 'Deepen connection',
    phrase: 'I am worthy of deep connection',
    icon: Heart,
    color: '#E8A4A4',
    gradient: 'from-rose-900/30 via-stone-900/50 to-[#121210]',
    image: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=800&q=80',
  },
  {
    id: 'body',
    title: 'Body',
    subtitle: 'Embody vitality',
    phrase: 'My body is my trusted companion',
    icon: Sun,
    color: '#7BA378',
    gradient: 'from-emerald-900/30 via-stone-900/50 to-[#121210]',
    image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80',
  },
  {
    id: 'performance',
    title: 'Performance',
    subtitle: 'Activate peak state',
    phrase: 'I am composed, focused, ready',
    icon: Zap,
    color: '#F59E0B',
    gradient: 'from-orange-900/30 via-stone-900/50 to-[#121210]',
    image: 'https://images.unsplash.com/photo-1517838277536-f5f99be501cd?w=800&q=80',
  },
  {
    id: 'home',
    title: 'Home',
    subtitle: 'Feel belonging',
    phrase: 'I belong in beautiful spaces',
    icon: Home,
    color: '#A89080',
    gradient: 'from-stone-800/30 via-stone-900/50 to-[#121210]',
    image: 'https://images.pexels.com/photos/931887/pexels-photo-931887.jpeg?w=800&q=80',
  },
  {
    id: 'freedom',
    title: 'Freedom',
    subtitle: 'Embrace expansion',
    phrase: 'I design my own path',
    icon: Compass,
    color: '#7BA3B8',
    gradient: 'from-sky-900/30 via-stone-900/50 to-[#121210]',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80',
  },
];

// Arrival Gate Component - the soft entry
const ArrivalGate = ({ onProceed, onReset }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#121210]"
    >
      <div className="max-w-md w-full px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <p className="text-[#9E9A90] text-xs uppercase tracking-[0.25em] mb-8 font-light">
            Before You Enter
          </p>
          
          <h2 className="font-['Playfair_Display'] text-3xl text-[#F2EFE9] mb-4 leading-relaxed">
            Let your body arrive with you
          </h2>
          
          <p className="text-[#9E9A90] text-base mb-16 font-light leading-relaxed">
            A moment of presence before you step into your reality
          </p>

          <motion.button
            onClick={onReset}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-5 rounded-2xl bg-[#1A1A18] border border-[#2C2C28] hover:border-[#4A6741]/40 transition-all duration-500 mb-4"
            data-testid="choose-reset-btn"
          >
            <span className="text-[#F2EFE9] text-lg font-light">Choose a Reset</span>
          </motion.button>

          <motion.button
            onClick={onProceed}
            className="text-[#9E9A90] text-sm hover:text-[#F2EFE9] transition-colors duration-500"
            data-testid="skip-reset-btn"
          >
            Skip for now
          </motion.button>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Reality Card Component
const RealityCard = ({ state, index, onSelect, streak }) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = state.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.8, 
        delay: 0.1 + index * 0.08,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      className="relative overflow-hidden rounded-2xl cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(state)}
      data-testid={`reality-${state.id}`}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <motion.img
          src={state.image}
          alt={state.title}
          className="w-full h-full object-cover"
          animate={{ 
            scale: isHovered ? 1.08 : 1,
            filter: isHovered ? 'brightness(1.1)' : 'brightness(0.9)'
          }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </div>

      {/* Gradient Overlay */}
      <div className={`absolute inset-0 bg-gradient-to-t ${state.gradient}`} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* Hover Glow */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          boxShadow: isHovered 
            ? `inset 0 0 60px ${state.color}20, 0 0 30px ${state.color}10`
            : 'inset 0 0 0px transparent'
        }}
        transition={{ duration: 0.6 }}
      />

      {/* Content */}
      <div className="relative h-full min-h-[200px] p-6 flex flex-col justify-end">
        <motion.div
          animate={{ 
            y: isHovered ? -6 : 0,
            opacity: isHovered ? 1 : 0.95
          }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <motion.div
              animate={{ 
                scale: isHovered ? 1.1 : 1,
                rotate: isHovered ? 5 : 0
              }}
              transition={{ duration: 0.4 }}
            >
              <Icon size={18} strokeWidth={1.5} style={{ color: state.color }} />
            </motion.div>
            <span className="text-[#9E9A90] text-xs uppercase tracking-[0.2em] font-light">
              {state.title}
            </span>
          </div>
          <p className="text-[#F2EFE9] text-sm font-light opacity-80">
            {state.subtitle}
          </p>
        </motion.div>
      </div>

      {/* Border Animation */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ border: `1px solid ${state.color}00` }}
        animate={{ 
          borderColor: isHovered ? `${state.color}40` : `${state.color}00`
        }}
        transition={{ duration: 0.5 }}
      />
    </motion.div>
  );
};

// Instant Alignment Entry — soft, distinct from reality cards
const InstantAlignmentEntry = ({ onSelect }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.25 }}
      className="relative overflow-hidden rounded-2xl cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onSelect}
      data-testid="instant-alignment-entry"
    >
      {/* Dark ambient background */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 60%, rgba(30,22,14,1) 0%, rgba(18,18,16,1) 80%)',
      }} />

      {/* Soft glow */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(210,175,130,0.04) 0%, transparent 60%)',
        }}
        animate={{
          opacity: isHovered ? 1 : 0.5,
          scale: isHovered ? 1.1 : 1,
        }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />

      {/* Content */}
      <div className="relative py-8 px-6 flex flex-col items-center text-center">
        <motion.div
          animate={{ opacity: isHovered ? 0.5 : 0.25 }}
          transition={{ duration: 0.6 }}
          className="mb-4"
        >
          <Waves size={20} strokeWidth={1} style={{ color: '#D2AF82' }} />
        </motion.div>

        <p className="text-[#9E9A90] text-xs uppercase tracking-[0.25em] mb-3 font-light">
          Instant Alignment
        </p>

        <motion.p
          className="text-[#F2EFE9]/60 text-sm font-light leading-relaxed max-w-xs"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          animate={{ opacity: isHovered ? 0.8 : 0.5 }}
          transition={{ duration: 0.5 }}
        >
          Close your eyes… step into the feeling
        </motion.p>
      </div>

      {/* Border */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ border: '1px solid rgba(210,175,130,0)' }}
        animate={{
          borderColor: isHovered ? 'rgba(210,175,130,0.15)' : 'rgba(210,175,130,0.04)',
        }}
        transition={{ duration: 0.5 }}
      />
    </motion.div>
  );
};

// Main Component
export const EnterYourReality = () => {
  const [showArrival, setShowArrival] = useState(true);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch streak on mount
  useEffect(() => {
    fetchStreak();
  }, []);

  const fetchStreak = async () => {
    const token = localStorage.getItem('reclaim_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/abundance/streak`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStreak(data.streak || 0);
      }
    } catch (error) {
      console.error('Failed to fetch streak:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSelection = () => {
    // Navigate to existing reset library
    navigate('/networker/resets');
  };

  const handleProceed = () => {
    setShowArrival(false);
  };

  const handleRealitySelect = (state) => {
    // Navigate to state immersion experience
    navigate(`/abundance/immerse/${state.id}`);
  };

  return (
    <div className="min-h-screen bg-[#121210]">
      {/* Subtle noise texture */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Arrival Gate */}
      <AnimatePresence>
        {showArrival && (
          <ArrivalGate 
            onProceed={handleProceed}
            onReset={handleResetSelection}
          />
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showArrival ? 0 : 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative z-10"
      >
        {/* Header */}
        <header className="px-6 pt-10 pb-6">
          <div className="flex items-center justify-between mb-8">
            {/* Streak Badge */}
            {streak > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1A1A18] border border-[#2C2C28]"
              >
                <TrendingUp size={14} className="text-[#4A6741]" />
                <span className="text-[#F2EFE9] text-sm font-light">{streak} days</span>
              </motion.div>
            )}
            
            {/* Edit Reality Button */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              onClick={() => navigate('/abundance/edit')}
              className="p-2 rounded-lg hover:bg-[#1A1A18] transition-colors duration-300"
              data-testid="edit-reality-btn"
            >
              <Settings size={20} strokeWidth={1.5} className="text-[#9E9A90]" />
            </motion.button>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-[#9E9A90] text-xs uppercase tracking-[0.25em] mb-4 font-light"
          >
            Daily Practice
          </motion.p>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-['Playfair_Display'] text-4xl text-[#F2EFE9] tracking-tight leading-tight"
          >
            Enter Your Reality
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-[#9E9A90] text-base mt-4 font-light"
          >
            Choose the state you want to embody today
          </motion.p>
        </header>

        {/* Reality Grid */}
        <main className="px-6 pb-32">
          {/* Instant Alignment — distinct entry */}
          <div className="mb-6">
            <InstantAlignmentEntry onSelect={() => navigate('/instant-alignment')} />
          </div>

          {/* Living Manifestation — user-created audio reality */}
          <motion.button
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            onClick={() => navigate('/abundance/manifestations')}
            className="w-full mb-8 rounded-2xl p-5 text-left transition-all"
            style={{
              background: "linear-gradient(135deg, rgba(203,161,83,0.1) 0%, rgba(30,26,20,1) 100%)",
              border: "1px solid rgba(203,161,83,0.22)",
            }}
            data-testid="living-manifestation-entry"
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: "radial-gradient(circle, rgba(203,161,83,0.2) 0%, transparent 70%)",
                  border: "1px solid rgba(203,161,83,0.25)",
                }}
              >
                <Sparkles size={20} strokeWidth={1.3} style={{ color: "rgba(203,161,83,0.85)" }} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.25em] font-light mb-1" style={{ color: "rgba(203,161,83,0.55)" }}>
                  Living Manifestation
                </p>
                <p className="text-base italic" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.88)" }}>
                  Your reality, in your own words
                </p>
                <p className="text-xs font-light mt-1" style={{ color: "rgba(245,240,230,0.4)" }}>
                  Write it. Speak it. Live it — 2 to 3 times today.
                </p>
              </div>
            </div>
          </motion.button>

          {/* Section label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-[#9E9A90] text-xs uppercase tracking-[0.25em] mb-4 font-light"
          >
            Vision Immersion
          </motion.p>

          <div className="grid grid-cols-2 gap-4">
            {REALITY_STATES.map((state, index) => (
              <RealityCard
                key={state.id}
                state={state}
                index={index}
                onSelect={handleRealitySelect}
                streak={streak}
              />
            ))}
          </div>
        </main>

        {/* Bottom Fade */}
        <div className="fixed bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#121210] to-transparent pointer-events-none" />
      </motion.div>
    </div>
  );
};

export default EnterYourReality;
